import type { AsyncLocalStorage as AsyncLocalStorageType } from 'async_hooks';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import getConfig from 'next/config';

import getServerSidePageLocale from './getServerSidePageLocale';
import { isServer } from './helpers';

export type HostAxiosConfig = {
  baseURL: string;
  xCKey: string;
};

type ServerRequestContext = HostAxiosConfig & {
  locale?: string;
};

const { publicRuntimeConfig } = getConfig();

type ConfigMatrixEntry = { api_endpoint?: string; x_c_key?: string };

let cachedConfigMatrix: { [key: string]: ConfigMatrixEntry } | null = null;

const parseConfigMatrix = (): { [key: string]: ConfigMatrixEntry } => {
  if (cachedConfigMatrix !== null) {
    return cachedConfigMatrix;
  }

  try {
    cachedConfigMatrix = JSON.parse(process.env.PK_CONFIG_MATRIX_JSON || '{}');
  } catch {
    // eslint-disable-next-line no-console -- intentional one-time warning for misconfiguration
    console.error(
      '[hostConfig] Failed to parse PK_CONFIG_MATRIX_JSON; falling back to {}'
    );
    cachedConfigMatrix = {};
  }

  return cachedConfigMatrix as { [key: string]: ConfigMatrixEntry };
};

export const getHostAxiosConfig = (host: string): HostAxiosConfig => {
  const configMatrix = parseConfigMatrix();
  const hostEntry = configMatrix[host];

  return {
    baseURL: hostEntry?.api_endpoint || publicRuntimeConfig.apiUrl,
    xCKey: hostEntry?.x_c_key || publicRuntimeConfig.xcKey,
  };
};

let hostConfigStorage: AsyncLocalStorageType<ServerRequestContext | null> | null =
  null;

if (isServer) {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- server-only
  const { AsyncLocalStorage } = require('async_hooks');

  hostConfigStorage =
    new AsyncLocalStorage() as AsyncLocalStorageType<ServerRequestContext | null>;
}

export const setServerHostConfig = (
  config: ServerRequestContext | null
): void => {
  hostConfigStorage?.enterWith(config);
};

export const getServerHostConfig = (): HostAxiosConfig | null =>
  hostConfigStorage?.getStore() ?? null;

export const getServerLocale = (): string | null =>
  hostConfigStorage?.getStore()?.locale ?? null;

export const getClientHostAxiosConfig = (): HostAxiosConfig | null => {
  if (isServer) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-underscore-dangle -- accessing Next.js internal page data
  const pageProps = (window as any).__NEXT_DATA__?.props?.pageProps;
  const baseURL = pageProps?.apiBaseUrl;
  const xCKey = pageProps?.xCKey;

  if (!baseURL && !xCKey) {
    return null;
  }

  return {
    baseURL: baseURL || publicRuntimeConfig.apiUrl,
    xCKey: xCKey || publicRuntimeConfig.xcKey,
  };
};

export const getApiBaseUrl = (): string => {
  if (isServer) {
    const serverConfig = getServerHostConfig();

    return serverConfig?.baseURL || publicRuntimeConfig.apiUrl || '';
  }

  const clientConfig = getClientHostAxiosConfig();

  return clientConfig?.baseURL || publicRuntimeConfig.apiUrl || '';
};

type AnyServerSidePropsFunction = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches Next.js signature
  context: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches Next.js signature
) => Promise<GetServerSidePropsResult<any>>;

export const withHostConfig = <T extends AnyServerSidePropsFunction>(
  gssp: T
): T =>
  ((context: GetServerSidePropsContext) => {
    const host = context.req.headers.host || '';
    const config = getHostAxiosConfig(host);
    const requestContext: ServerRequestContext = {
      ...config,
      locale: getServerSidePageLocale(context),
    };

    if (hostConfigStorage) {
      return hostConfigStorage.run(requestContext, () => gssp(context));
    }

    // eslint-disable-next-line no-console -- warn on unexpected missing storage; host config will not be scoped per-request
    console.warn(
      '[hostConfig] AsyncLocalStorage is not available; host config will not be scoped per-request'
    );

    return gssp(context);
  }) as unknown as T;
